import React from 'react'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles(() => ({
  latestReadings: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    position: 'sticky',
    top: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    '& h3': {
      fontSize: '14px',
      color: '#111827',
      marginBottom: '12px',
      fontWeight: 600,
    },
  },
  readingSelector: {
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '& label': {
      fontSize: '11px',
      color: '#6b7280',
      whiteSpace: 'nowrap',
    },
    '& select': {
      flex: 1,
      padding: '6px 8px',
      background: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      color: '#111827',
      fontSize: '12px',
      outline: 'none',
    },
  },
  tableScroll: {
    overflowX: 'auto',
    '& table': {
      borderCollapse: 'collapse',
      fontSize: '12px',
      whiteSpace: 'nowrap',
      width: '100%',
    },
    '& th': {
      textAlign: 'right',
      padding: '6px 8px',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
      fontWeight: 500,
      background: '#f9fafb',
    },
    '& th:first-child': { textAlign: 'left' },
    '& td': {
      textAlign: 'right',
      padding: '6px 8px',
      color: '#374151',
      borderBottom: '1px solid #f3f4f6',
    },
    '& td:first-child': { textAlign: 'left', fontWeight: 500 },
  },
  lastSeen: {
    marginTop: '12px',
    fontSize: '11px',
    color: '#6b7280',
  },
}))

export default function LatestReadings({ telemetryDocs, phases, pinnedReadingTs, onSelectReading }) {
  const classes = useStyles()

  if (!telemetryDocs || telemetryDocs.length === 0) return null

  let docIndex = telemetryDocs.length - 1
  if (pinnedReadingTs) {
    const found = telemetryDocs.findIndex((doc) => doc.receivedAt === pinnedReadingTs)
    if (found !== -1) docIndex = found
  }

  const doc = telemetryDocs[docIndex]
  const reading = doc.telemetry
  const isPinned = pinnedReadingTs !== null

  const getValue = (key, phase, field) => {
    const arr = reading?.[key]
    if (!arr) return null
    const channel = arr.find((item) => item.channel === phase)
    return channel?.[field] ?? null
  }

  const fmt = (value, divisor = 1) => {
    if (value === null || value === undefined) return '-'
    return (value / divisor).toFixed(1)
  }

  const fmtRaw = (value, divisor = 1) => {
    if (value === null || value === undefined) return '-'
    return value / divisor
  }

  const fmtEnergy = (value) => {
    if (value === null || value === undefined) return '-'
    return Number(value).toLocaleString()
  }

  const formatTs = (isoStr) => {
    const date = new Date(isoStr)
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
  }

  const handleDropdownChange = (event) => {
    onSelectReading(Number(event.target.value))
  }

  const dropdownValue = isPinned ? docIndex : -1

  return (
    <div className={classes.latestReadings}>
      <h3>{isPinned ? 'Pinned Reading' : 'Latest Readings'} (per phase)</h3>
      <div className={classes.readingSelector}>
        <label>Select reading:</label>
        <select value={dropdownValue} onChange={handleDropdownChange}>
          <option value={-1}>Latest ({formatTs(telemetryDocs[telemetryDocs.length - 1].receivedAt)})</option>
          {[...telemetryDocs].map((doc, i) => telemetryDocs.length - 1 - i).map((index) => (
            <option key={index} value={index}>{formatTs(telemetryDocs[index].receivedAt)}</option>
          ))}
        </select>
      </div>
      <div className={classes.tableScroll}>
        <table>
          <thead>
            <tr>
              <th>Phase</th>
              <th>Grid I (A)</th>
              <th>Con P (W)</th>
              <th>Pro P (W)</th>
              <th>Charge (W)</th>
              <th>Discharge (W)</th>
              <th>Grid P (W)</th>
              <th>Grid S (VA)</th>
              <th>Cha E (Wh)</th>
              <th>Dis E (Wh)</th>
              <th>PF</th>
              <th>SOC (%)</th>
            </tr>
          </thead>
          <tbody>
            {phases.map((phase, index) => (
              <tr key={phase}>
                <td>L{phase}</td>
                <td>{fmt(getValue('gridCMa', phase, 'current'), 1000)}</td>
                <td>{fmt(getValue('conPMw', phase, 'power'), 1000)}</td>
                <td>{fmt(getValue('proPMw', phase, 'power'), 1000)}</td>
                <td>{fmt(getValue('chaPMw', phase, 'power'), 1000)}</td>
                <td>{fmt(getValue('disPMw', phase, 'power'), 1000)}</td>
                <td>{fmt(getValue('gridPMw', phase, 'power'), 1000)}</td>
                <td>{fmt(getValue('gridSMva', phase, 'power'), 1000)}</td>
                <td>{fmtEnergy(getValue('chaEWh', phase, 'energy'))}</td>
                <td>{fmtEnergy(getValue('disEWh', phase, 'energy'))}</td>
                <td>{fmtRaw(getValue('gridPf', phase, 'powerFactor'), 1000)}</td>
                {index === 0 && <td rowSpan={phases.length}>{reading?.socPer != null ? reading.socPer : '-'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={classes.lastSeen}>
        Showing: {formatTs(doc.receivedAt)} {isPinned && '(pinned)'}
      </div>
    </div>
  )
}
